// Expense analytics: three plain-SVG/HTML charts (no chart library).
//  - Expense Trend        : area+line of daily totals
//  - Expense by Category   : donut of amount by `type`, legend gives identity
//  - Top Expenses by Case  : horizontal bars, top 4 + Others
//
// Categorical hues are the dataviz reference palette (validated: worst adjacent
// CVD ΔE 24.2). aqua/yellow are sub-3:1 on white, so the donut ALWAYS ships its
// legend (labels + %) — identity is never color-alone.

const CAT_COLORS = [
  "#2a78d6", // blue
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#008300", // green
  "#4a3aa7", // violet
  "#e34948", // red
  "#e87ba4", // magenta
  "#eb6834", // orange
];
const OTHER_COLOR = "#94a3b8";
const ACCENT = "#2a78d6";

const catColor = (i) => (i < CAT_COLORS.length ? CAT_COLORS[i] : OTHER_COLOR);

function ChartCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function EmptyChart({ label = "No data yet" }) {
  return (
    <div className="flex h-40 items-center justify-center text-sm text-slate-400">
      {label}
    </div>
  );
}

export default function ExpenseAnalytics({ expenses = [], formatAmount }) {
  const inr =
    formatAmount ||
    ((n) => "₹" + Number(n || 0).toLocaleString("en-IN"));
  const amt = (e) => parseFloat(e.amount) || 0;

  // ---- Category aggregation (donut) ----
  const catMap = {};
  let total = 0;
  expenses.forEach((e) => {
    const a = amt(e);
    total += a;
    const k = e.type || "Uncategorized";
    catMap[k] = (catMap[k] || 0) + a;
  });
  let cats = Object.entries(catMap)
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => b.amount - a.amount);
  if (cats.length > 8) {
    const top = cats.slice(0, 7);
    const other = cats.slice(7).reduce((s, c) => s + c.amount, 0);
    top.push({ name: "Other", amount: other });
    cats = top;
  }

  // ---- Case aggregation (bars) ----
  const caseMap = {};
  expenses.forEach((e) => {
    const k = e.caseName || "General Expense";
    if (!caseMap[k]) caseMap[k] = { amount: 0, caseNo: e.caseNo };
    caseMap[k].amount += amt(e);
  });
  const allCases = Object.entries(caseMap)
    .map(([name, v]) => ({ name, caseNo: v.caseNo, amount: v.amount }))
    .sort((a, b) => b.amount - a.amount);
  const topCases = allCases.slice(0, 4);
  const otherCasesAmt = allCases.slice(4).reduce((s, c) => s + c.amount, 0);
  const barMax = Math.max(...allCases.map((c) => c.amount), 1);

  // ---- Daily trend (area/line) ----
  const byDay = {};
  expenses.forEach((e) => {
    const raw = e.date || (e.createdAt ? String(e.createdAt).slice(0, 10) : null);
    if (!raw) return;
    const dt = new Date(raw);
    if (isNaN(dt.getTime())) return;
    const key = dt.toISOString().slice(0, 10);
    byDay[key] = (byDay[key] || 0) + amt(e);
  });
  const trend = Object.entries(byDay)
    .map(([date, amount]) => ({ date, amount, t: new Date(date).getTime() }))
    .sort((a, b) => a.t - b.t);

  // ---- Donut geometry ----
  const R = 54;
  const SW = 18;
  const C = 2 * Math.PI * R;
  let acc = 0;

  // Center total: no decimals (shorter) + font sized to the string length so a
  // long bold amount (e.g. ₹1,80,000) never overflows the ring or cramps.
  const centerStr = "₹" + Math.round(total).toLocaleString("en-IN");
  const centerFont =
    centerStr.length <= 9 ? 15 : centerStr.length <= 12 ? 12.5 : centerStr.length <= 15 ? 10.5 : 9;

  // ---- Trend geometry ----
  const W = 320;
  const H = 150;
  const padL = 12;
  const padR = 12;
  const padT = 12;
  const padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const maxAmt = Math.max(...trend.map((p) => p.amount), 1);
  const n = trend.length;
  const xFor = (i) => (n <= 1 ? padL + innerW / 2 : padL + (i / (n - 1)) * innerW);
  const yFor = (v) => padT + innerH - (v / maxAmt) * innerH;
  const linePoints = trend.map((p, i) => `${xFor(i)},${yFor(p.amount)}`).join(" ");
  const areaPath =
    n >= 2
      ? `M ${xFor(0)},${padT + innerH} ` +
        trend.map((p, i) => `L ${xFor(i)},${yFor(p.amount)}`).join(" ") +
        ` L ${xFor(n - 1)},${padT + innerH} Z`
      : "";
  const fmtDay = (t) =>
    new Date(t).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {/* ===== Expense Trend ===== */}
      <ChartCard title="Expense Trend">
        {trend.length === 0 ? (
          <EmptyChart />
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="h-40 w-full" role="img" aria-label="Expense trend over time">
            {/* horizontal gridlines */}
            {[0, 0.5, 1].map((f) => {
              const y = padT + innerH - f * innerH;
              return (
                <g key={f}>
                  <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e2e8f0" strokeWidth="1" />
                  <text x={padL - 2} y={y - 2} fontSize="8" fill="#94a3b8">
                    {Math.round((f * maxAmt) / 1000)}k
                  </text>
                </g>
              );
            })}
            {areaPath && <path d={areaPath} fill={ACCENT} fillOpacity="0.12" />}
            {n >= 2 && (
              <polyline points={linePoints} fill="none" stroke={ACCENT} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            )}
            {trend.map((p, i) => (
              <circle key={i} cx={xFor(i)} cy={yFor(p.amount)} r="3" fill={ACCENT} stroke="#fff" strokeWidth="1.5">
                <title>{`${fmtDay(p.t)}: ${inr(p.amount)}`}</title>
              </circle>
            ))}
            {/* x labels: first, middle, last */}
            {[0, Math.floor((n - 1) / 2), n - 1]
              .filter((v, idx, arr) => arr.indexOf(v) === idx && v >= 0)
              .map((i) => (
                <text key={i} x={xFor(i)} y={H - 6} fontSize="8" fill="#94a3b8" textAnchor="middle">
                  {fmtDay(trend[i].t)}
                </text>
              ))}
          </svg>
        )}
      </ChartCard>

      {/* ===== Expense by Category ===== */}
      <ChartCard title="Expense by Category">
        {total === 0 ? (
          <EmptyChart />
        ) : (
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <svg viewBox="0 0 140 140" className="h-36 w-36 shrink-0" role="img" aria-label="Expense by category">
              <g transform="rotate(-90 70 70)">
                <circle cx="70" cy="70" r={R} fill="none" stroke="#eef2f6" strokeWidth={SW} />
                {cats.map((c, i) => {
                  const frac = total > 0 ? c.amount / total : 0;
                  const seg = frac * C;
                  const len = Math.max(0, seg - 2); // 2px gap between segments
                  const el = (
                    <circle
                      key={c.name}
                      cx="70"
                      cy="70"
                      r={R}
                      fill="none"
                      stroke={catColor(i)}
                      strokeWidth={SW}
                      strokeDasharray={`${len} ${C - len}`}
                      strokeDashoffset={-acc}
                    >
                      <title>{`${c.name}: ${inr(c.amount)} (${Math.round(frac * 100)}%)`}</title>
                    </circle>
                  );
                  acc += seg;
                  return el;
                })}
              </g>
              <text
                x="70"
                y="67"
                textAnchor="middle"
                fontSize={centerFont}
                fontWeight="700"
                fill="#0f172a"
                style={{ letterSpacing: "0.2px" }}
              >
                {centerStr}
              </text>
              <text x="70" y="83" textAnchor="middle" fontSize="9" fill="#94a3b8">
                Total
              </text>
            </svg>
            {/* legend = identity channel (relief for sub-3:1 hues) */}
            <ul className="w-full space-y-1.5">
              {cats.map((c, i) => (
                <li key={c.name} className="flex items-center gap-2 text-sm">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: catColor(i) }} />
                  <span className="truncate text-slate-600">{c.name}</span>
                  <span className="ml-auto font-medium text-slate-500">
                    {total > 0 ? Math.round((c.amount / total) * 100) : 0}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </ChartCard>

      {/* ===== Top Expenses by Case ===== */}
      <ChartCard title="Top Expenses by Case">
        {allCases.length === 0 ? (
          <EmptyChart />
        ) : (
          <div className="space-y-3">
            {topCases.map((c) => (
              <div key={c.name}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="truncate text-slate-700" title={c.name}>
                    {c.caseNo || c.name}
                  </span>
                  <span className="ml-2 font-semibold text-slate-900" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {inr(c.amount)}
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full" style={{ width: `${(c.amount / barMax) * 100}%`, backgroundColor: ACCENT }} />
                </div>
              </div>
            ))}
            {otherCasesAmt > 0 && (
              <div>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-slate-500">Others</span>
                  <span className="ml-2 font-semibold text-slate-700" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {inr(otherCasesAmt)}
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-slate-300" style={{ width: `${(otherCasesAmt / barMax) * 100}%` }} />
                </div>
              </div>
            )}
          </div>
        )}
      </ChartCard>
    </div>
  );
}
